import * as Sentry from "@sentry/react";
import { logError } from "ErrorService";
import {
  type AuthAction,
  type AuthState,
  authReducer,
} from "State/Auth/AuthReducer";
import { resetPasswordSaga } from "State/Auth/Passwords/ResetPasswordState";
import { setPasswordSaga } from "State/Auth/Passwords/SetPasswordsState";
import { resetUserSaga } from "State/Auth/ResetUser/ResetUserState";
import { signInSsoSaga } from "State/Auth/SignIn/SignInSsoState";
import { signInSaga } from "State/Auth/SignIn/SignInState";
import { signUpPasswordlessSaga } from "State/Auth/SignUp/SignUpPasswordlessState";
import { signUpSaga } from "State/Auth/SignUp/SignUpState";
import { getUserInfoSaga } from "State/Auth/UserInfo/GetUserInfoState";
import { verifyEmailSaga } from "State/Auth/Verifications/EmailVerificationState";
import { sendPhoneVerificationCodeSaga } from "State/Auth/Verifications/SendPhoneVerificationTokenState";
import { verifyPhoneSaga } from "State/Auth/Verifications/VerifyPhoneState";
import { balanceReducer } from "State/Balance/BalanceReducer";
import {
  getBalanceSaga,
  sellSaga,
  withdrawSaga,
} from "State/Balance/BalanceSaga";
import { codeListReducer } from "State/CodeLists/CodeListReducer";
import { getCodeListCollectionSaga } from "State/CodeLists/CodeListSaga";
import { getCategoryListSaga } from "State/Coinback/Categories/CategoryListState";
import { coinbackReducer } from "State/Coinback/CoinbackReducer";
import { getOfferDetailSaga } from "State/Coinback/OfferDetail/OfferDetailState";
import { getOfferListSaga } from "State/Coinback/OfferList/OfferListState";
import { errorSaga } from "State/Errors/ErrorsSaga";
import { createInvoiceSaga } from "State/Invoices/Create/CreateInvoiceState";
import { downloadEmployeeGuideSaga } from "State/Invoices/DownloadEmployerGuideline/DownloadEmployerGuidelineState";
import { invoiceReducer } from "State/Invoices/InvoiceReducer";
import {
  getInvoiceSettingsSaga,
  saveInvoiceSettingsSaga,
} from "State/Invoices/InvoiceSettings/InvoiceSettingsState";
import { getInvoiceListSaga } from "State/Invoices/List/GetInvoiceListState";
import { getInvoiceRateChangeSaga } from "State/Invoices/RateChanges/GetInvoiceRateChangeState";
import { processInvoiceRateChangeSaga } from "State/Invoices/RateChanges/ProcessInvoiceRateChangeState";
import { getUserFeeRateSaga } from "State/Invoices/UserFeeRate/GetUserFeeRateState";
import { notificationReducer } from "State/Notification/NotificationReducer";
import {
  getUserNotificationSettingsSaga,
  saveUserNotificationSettingsSaga,
} from "State/Notification/Settings/UserNotificationSettingsState";
import { getRatesHistorySaga } from "State/Rates/GerRatesHistoryState";
import { rateReducer } from "State/Rates/RateReducer";
import { getRateSaga } from "State/Rates/RateSaga";
import { generateSystemAddressSaga } from "State/System/ColdWallet/GenerateSystemAddressState";
import { systemWithdrawSaga } from "State/System/ColdWallet/SystemWithdrawalState";
import { systemReducer } from "State/System/SystemReducer";
import { getSystemReportSaga } from "State/System/SystemReport/SystemReportState";
import { getSystemSettingSaga } from "State/System/SystemSettings/GetSystemSettingState";
import { setSystemSettingSaga } from "State/System/SystemSettings/SaveSystemSettingState";
import { getTransactionDetailSaga } from "State/Transactions/Detail/TransactionDetailState";
import { getTransactionListSaga } from "State/Transactions/List/GetTransactionListState";
import { transactionReducer } from "State/Transactions/TransactionReducer";
import { uploadReducer } from "State/Upload/UploadReducer";
import { deactivateUserSaga } from "State/Users/Detail/DeactivateUserState";
import { userReducer } from "State/Users/UserReducer";
import { processBankIDVerificationSaga } from "State/Verification/BankID/ProcessBankIDVerificationState";
import { verificationReducer } from "State/Verification/VerificationReducer";
import { applyVoucherSaga } from "State/Voucher/ApplyVoucherState";
import { voucherReducer } from "State/Voucher/VoucherReducer";
import { nameof } from "Utils/ObjectUtils";
import { applyMiddleware, combineReducers, compose, createStore } from "redux";
import {
  type PersistConfig,
  persistReducer,
  persistStore,
} from "redux-persist";
import storage from "redux-persist/lib/storage";
import createSagaMiddleware from "redux-saga";
import { all } from "redux-saga/effects";

const authPersistConfig: PersistConfig<any> = {
  key: "auth",
  storage: storage,
  whitelist: [
    nameof<AuthState>("user"),
    nameof<AuthState>("unauthenticatedUrl"),
    nameof<AuthState>("verificationEmail"),
  ],
};

const authPersistReducer = persistReducer(
  authPersistConfig,
  authReducer,
) as unknown;

/**
 * Create global state of application
 */
const combinedReducer = combineReducers({
  auth: authPersistReducer as typeof authReducer,
  invoice: invoiceReducer,
  balance: balanceReducer,
  transaction: transactionReducer,
  rate: rateReducer,
  verification: verificationReducer,
  system: systemReducer,
  notification: notificationReducer,
  codeList: persistReducer(
    { key: "codeList", storage: storage },
    codeListReducer,
  ),
  upload: uploadReducer,
  user: userReducer,
  voucher: voucherReducer,
  coinback: coinbackReducer,
});

/**
 * Type of global application state
 */
export type RootStateType = ReturnType<typeof combinedReducer>;

/**
 * All changes, that global application state can handle
 */
type RootActionType = AuthAction;

const rootReducer = (state: RootStateType, action: RootActionType) =>
  combinedReducer(state, action);

const persistConfig = {
  key: "root",
  storage,
  whitelist: [],
};

const persistedReducer = persistReducer(persistConfig, rootReducer as any);

const sagaMiddleware = createSagaMiddleware({
  onError: (error, errorInfo) => {
    logError(error, errorInfo);
  },
});

const sentryReduxEnhancer = Sentry.createReduxEnhancer({});

const middlewares = [sagaMiddleware];
const composeEnhancers: any =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__?.({
    trace: true,
    traceLimit: 25,
  }) || compose;

const store = createStore(
  persistedReducer,
  composeEnhancers(applyMiddleware(...middlewares), sentryReduxEnhancer),
);

function* rootSaga() {
  yield all([
    signUpSaga(),
    signInSaga(),
    signInSsoSaga(),
    signUpPasswordlessSaga(),
    resetPasswordSaga(),
    setPasswordSaga(),
    sendPhoneVerificationCodeSaga(),
    verifyPhoneSaga(),
    verifyEmailSaga(),
    getBalanceSaga(),
    getTransactionListSaga(),
    withdrawSaga(),
    getInvoiceListSaga(),
    getRateSaga(),
    createInvoiceSaga(),
    getInvoiceRateChangeSaga(),
    processInvoiceRateChangeSaga(),
    downloadEmployeeGuideSaga(),
    getInvoiceSettingsSaga(),
    saveInvoiceSettingsSaga(),
    getCodeListCollectionSaga(),
    getUserFeeRateSaga(),
    getSystemReportSaga(),
    getUserNotificationSettingsSaga(),
    saveUserNotificationSettingsSaga(),
    getTransactionDetailSaga(),
    processBankIDVerificationSaga(),
    systemWithdrawSaga(),
    generateSystemAddressSaga(),
    getSystemSettingSaga(),
    setSystemSettingSaga(),
    resetUserSaga(),
    sellSaga(),
    applyVoucherSaga(),
    deactivateUserSaga(),
    getOfferListSaga(),
    getCategoryListSaga(),
    getRatesHistorySaga(),
    errorSaga(),
    getOfferDetailSaga(),
    getUserInfoSaga(),
  ]);
}

sagaMiddleware.run(rootSaga);

export const buildAppStore = () => {
  const persistor = persistStore(store as any);
  return { store, persistor };
};

export default buildAppStore();
